import { Flex, Text, Title } from '@mantine/core';
import { FC, Fragment, useEffect, useState } from 'react';
import { ReportedSiteList, StickyBar } from 'src/components';
import { shallowEqual } from 'react-redux';
import { fetchMyReports, fetchMyReportsCount, useAppDispatch, useAppSelector, fetchMyReportsEnhancedSearch } from 'src/store';
import { FilterMyReports } from 'src/components/filters';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components';
import { useDocumentTitle } from '@mantine/hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { defaultFilters } from 'src/constants/statuses';

import styles from './styles.module.scss';

const MyReportsPage: FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<{ [key: string]: string | null }>(
    searchParams.toString().length
      ? Object.assign({}, defaultFilters, Object.fromEntries(new URLSearchParams(location.search)))
      : defaultFilters,
  );
  const myReports = useAppSelector((state) => state.myReports, shallowEqual);
  const myReportsCount = useAppSelector((state) => state.myReportsCount.data?.issuesCount, shallowEqual);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean>(false);
  const [t] = useTranslation('myReports');
  const [c] = useTranslation('common');
  const onInstallExtensionHandler = (): Window | null => window.open('https://chrome.google.com/webstore/', '_blank');

  const getUserReportedIssuesSearch = (): void => {
    const filteredQuery = Object.keys(searchQuery).reduce<Record<string, string>>((prev, current) => {
      if (searchQuery[current] && searchQuery[current] !== 'all') prev[current] = searchQuery[current] as string;
      return prev;
    }, {});
    const issueStatus = searchQuery?.status === 'all' ? '' : searchQuery?.status
    let searchText = searchQuery?.searchString
    if (searchQuery?.searchType === 'settings') {
        searchText = searchQuery?.settings
    }
    if (searchQuery?.searchType === 'type') {
      searchText = searchQuery?.issueType
    }

    dispatch(fetchMyReportsEnhancedSearch({searchType: searchQuery?.searchType, 
      search: searchText, status: issueStatus, dateFrom: searchQuery?.dateFrom, dateTo: searchQuery?.dateTo}));
    setSearchParams({ ...filteredQuery});
  };

  useDocumentTitle(t('myReportsPageTitle'));

  useEffect(() => {
    getUserReportedIssuesSearch();
  }, [searchQuery]);

  useEffect(() => {
    dispatch(fetchMyReports());
    dispatch(fetchMyReportsCount());
  }, []);

  return (
    <Flex style={{ flexGrow: 1 }} direction={'column'} bg={'#F6FAFA'} my={0} px={24} py={24}>
      <Title id={'mainContent'} weight={700} style={{ marginBottom: 20 }} tabIndex={-1}>
        {myReportsCount} {t('title')}
      </Title>
      {myReports?.data?.items?.length === 0 && !isActiveFilter ? (
        <Flex align={'center'} direction={'column'}>
          <Text align="center" w={420} pt={90} pb={20}>
            {t('noReportsText')}
          </Text>
          <Button
            onClick={onInstallExtensionHandler}
            ariaLabel={`${t('installExtensionButton')} ${c('opensInNewTab')}`}>
            {t('installExtensionButton')}
          </Button>
        </Flex>
      ) : (
        <Fragment>
          <FilterMyReports
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            setDefault={() => setSearchQuery(defaultFilters)} 
            onIsActive={(v) => {
              setIsActiveFilter(v);
            }}
          />
          { myReports?.data?.items?.length > 0 ? (
          <ReportedSiteList
            userRole={'user'}
            issues={myReports.data.items}
            nextToken={myReports.data.nextToken}
            onLoadMore={() => dispatch(fetchMyReportsEnhancedSearch({ nextToken: myReports.data.nextToken }))}
          />) : (
            <Fragment>
              <StickyBar className={styles.stickyBar}>
                <div>{t('noSearchResults')}</div>
              </StickyBar>
            </Fragment>) }
        </Fragment>
      )}
    </Flex>
  );
};

export default MyReportsPage;
